import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchQuery, useSearchFilters, useSearchResults, useSearchPage } from '../contexts/SearchContext';
import { usePushIndexUiState } from '../hooks/usePushIndexUiState';
import { navigate } from 'vike/client/router';
import { getIndexName } from '../constants';
import { generateUrlFromIndexUiState } from '../utils/algolia/url-handling';
import { extractFacetsFromSearchResults } from '../utils/facets/facets-shared';
import { useFacets } from '../contexts/FacetsContext';
import { formatFilterLabel } from '../utils/facets/format-filter-label';
import { 
  Search, 
  X, 
  Briefcase, 
  MapPin, 
  Building2, 
  Code, 
  Palette, 
  LineChart, 
  Heart, 
  GraduationCap, 
  ShieldCheck, 
  Hammer, 
  Truck, 
  Users, 
  Sparkles,
  Lightbulb,
  ArrowRight
} from 'lucide-react';

// iOS26 style utility classes
const iosFontStack = "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";

/**
 * Generate query suggestions from facets based on current query
 */
function generateSuggestionsFromFacets(
  facetsData: { [key: string]: Array<{ value: string; label: string; count: number }> },
  currentQuery: string
): Array<{ query: string; count: number }> {
  const suggestions: Array<{ query: string; count: number }> = [];
  const queryLower = currentQuery.toLowerCase().trim();
  
  // Priority facets for suggestions
  const suggestionFacets = ['normalisedTitle', 'skillsAndTechnologies', 'field'];
  
  suggestionFacets.forEach(facetName => {
    const items = facetsData[facetName] || [];
    items.forEach(item => {
      const label = item.label || item.value;
      const labelLower = label.toLowerCase();
      
      // If there's a query, only include suggestions that match (loose matching)
      if (queryLower) {
        // Check if the label contains any word from the query
        const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
        const labelWords = labelLower.split(/\s+/);
        
        const hasMatch = queryWords.some(qw => 
          labelWords.some(lw => lw.includes(qw) || qw.includes(lw))
        );
        
        if (!hasMatch) return;
      }
      
      // Avoid duplicates
      if (!suggestions.find(s => s.query.toLowerCase() === labelLower)) {
        suggestions.push({
          query: label,
          count: item.count,
        });
      }
    });
  });
  
  // Sort by relevance (if query exists, prioritize matches) and count
  return suggestions
    .sort((a, b) => {
      if (queryLower) {
        const aLower = a.query.toLowerCase();
        const bLower = b.query.toLowerCase();
        const aStartsWith = aLower.startsWith(queryLower);
        const bStartsWith = bLower.startsWith(queryLower);
        const aContains = aLower.includes(queryLower);
        const bContains = bLower.includes(queryLower);
        
        // Prioritize: starts with > contains > count
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        if (aContains && !bContains) return -1;
        if (!aContains && bContains) return 1;
      }
      return b.count - a.count;
    })
    .slice(0, 6); // Limit to 6 suggestions
}

/**
 * Get icon for industry
 */
function getIndustryIcon(industry: string): React.ReactNode {
  const industryLower = industry.toLowerCase();
  
  if (industryLower.includes('tech') || industryLower.includes('software') || industryLower.includes('it')) {
    return <Code size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('design') || industryLower.includes('creative')) {
    return <Palette size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('finance') || industryLower.includes('banking')) {
    return <LineChart size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('healthcare') || industryLower.includes('health') || industryLower.includes('medical')) {
    return <Heart size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('education') || industryLower.includes('training')) {
    return <GraduationCap size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('security') || industryLower.includes('cyber')) {
    return <ShieldCheck size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('construction') || industryLower.includes('engineering')) {
    return <Hammer size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('logistics') || industryLower.includes('transport')) {
    return <Truck size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (industryLower.includes('consulting') || industryLower.includes('management')) {
    return <Users size={16} className="text-green-600 dark:text-green-400" />;
  }
  return <Building2 size={16} className="text-green-600 dark:text-green-400" />;
}

/**
 * Get icon for job role
 */
function getRoleIcon(role: string): React.ReactNode {
  const roleLower = role.toLowerCase();
  
  if (roleLower.includes('developer') || roleLower.includes('engineer') || roleLower.includes('programmer')) {
    return <Code size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (roleLower.includes('designer') || roleLower.includes('ux') || roleLower.includes('ui')) {
    return <Palette size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (roleLower.includes('manager') || roleLower.includes('director') || roleLower.includes('lead')) {
    return <Users size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (roleLower.includes('analyst') || roleLower.includes('data')) {
    return <LineChart size={16} className="text-green-600 dark:text-green-400" />;
  }
  if (roleLower.includes('devops') || roleLower.includes('infrastructure') || roleLower.includes('cloud')) {
    return <Sparkles size={16} className="text-green-600 dark:text-green-400" />;
  }
  return <Briefcase size={16} className="text-green-600 dark:text-green-400" />;
}

/**
 * Extracts the top N items from raw Algolia facets
 */
function getTopFacetItemsFromRawFacets(
  facets: Record<string, Record<string, number>> | undefined,
  facetName: string,
  limit: number = 5
): Array<{ label: string; count: number }> {
  if (!facets || !facets[facetName]) {
    return [];
  }
  const facetData = facets[facetName];
  return Object.entries(facetData)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

/**
 * Extracts the top N items from processed facets data
 */
function getTopFacetItems(
  facetsData: { [key: string]: Array<{ value: string; label: string; count: number }> },
  facetName: string,
  limit: number = 5
): Array<{ label: string; count: number }> {
  const items = facetsData[facetName] || [];
  return items
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(item => ({ label: item.label, count: item.count }));
}

interface NoResultsProps {
  query?: string;
}

export function NoResults({ query: queryProp }: NoResultsProps) {
  const intl = useIntl();
  const { query: contextQuery, setQuery } = useSearchQuery();
  const { filters } = useSearchFilters();
  const { results } = useSearchResults();
  const { page } = useSearchPage();
  const { pushIndexUiState } = usePushIndexUiState();
  
  // Use query from prop or context
  const query = queryProp || contextQuery;
  
  // Check if we're on a page > 1
  const isNotFirstPage = page > 0;

  // Parse filters
  const filtersObj = useMemo(() => {
    if (!filters) return {};
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return filters as Record<string, string[]>;
  }, [filters]);

  // Check if any filters are applied
  const hasFilters = useMemo(() => {
    return Object.keys(filtersObj).length > 0;
  }, [filtersObj]);

  // Try to get facets from FacetsContext (populated from SSR)
  const { facetsData: contextFacetsData } = useFacets();

  // Extract facets from search results (already available from SRP)
  // Fallback to context facets if results don't have facets
  const facetsData = useMemo(() => {
    if (results?.facets && Object.keys(results.facets).length > 0) {
      const extracted = extractFacetsFromSearchResults(results, filtersObj);
      // If we got facets from results, use them
      if (Object.keys(extracted).length > 0) {
        return extracted;
      }
    }
    // Fallback to context facets (from SSR)
    return contextFacetsData || {};
  }, [results, filtersObj, contextFacetsData]);

  // Get raw facets for direct access
  const rawFacets = results?.facets;

  // Get popular categories from facets
  const popularIndustries = useMemo(() => {
    if (rawFacets) {
      return getTopFacetItemsFromRawFacets(rawFacets, 'companyIndustry', 6);
    }
    return getTopFacetItems(facetsData, 'companyIndustry', 6);
  }, [rawFacets, facetsData]);
  
  const popularRoles = useMemo(() => {
    if (rawFacets) {
      return getTopFacetItemsFromRawFacets(rawFacets, 'normalisedTitle', 6);
    }
    return getTopFacetItems(facetsData, 'normalisedTitle', 6);
  }, [rawFacets, facetsData]);
  
  const popularLocations = useMemo(() => {
    if (rawFacets) {
      return getTopFacetItemsFromRawFacets(rawFacets, 'location', 6);
    }
    return getTopFacetItems(facetsData, 'location', 6);
  }, [rawFacets, facetsData]);

  // Generate suggestions from facets
  const suggestions = useMemo(() => {
    return generateSuggestionsFromFacets(facetsData, query || '');
  }, [facetsData, query]);

  // Handler for clicking a suggestion: replace the query in the search state
  const handleSuggestionClick = (suggestionQuery: string) => {
    setQuery(suggestionQuery);
  };

  // Handler for applying a filter
  const handleFilterClick = (attribute: string, value: string) => {
    const currentValues = filtersObj[attribute] || [];
    const newFilters = {
      ...filtersObj,
      [attribute]: [...currentValues, value],
    };
    pushIndexUiState({
      query: query || '',
      refinementList: newFilters,
      page: 0,
    });
  };

  // Handler for removing a specific filter
  const handleRemoveFilter = (attribute: string, value: string) => {
    const currentValues = filtersObj[attribute] || [];
    const newValues = currentValues.filter(v => v !== value);
    const newFilters = { ...filtersObj };
    if (newValues.length === 0) {
      delete newFilters[attribute];
    } else {
      newFilters[attribute] = newValues;
    }
    pushIndexUiState({
      query: query || '',
      refinementList: newFilters,
      page: 0,
    });
  };

  // Handler for clearing the query
  const handleClearQuery = () => {
    pushIndexUiState({
      query: '',
      refinementList: filtersObj,
      page: 0,
    });
  };

  // Handler for going to first page
  const handleGoToFirstPage = () => {
    pushIndexUiState({
      query: query || '',
      refinementList: filtersObj,
      page: 0,
    });
  };

  // Handler for clearing all search filters and query
  const handleClearSearch = async () => {
    try {
      const indexName = getIndexName(intl.locale);
      const clearedState = {
        query: '',
        refinementList: {},
        page: 0,
        sort: 'relevance' as const,
      };
      
      // Update state immediately
      pushIndexUiState(clearedState);
      
      // Also navigate using Vike's router for proper routing
      const url = generateUrlFromIndexUiState(clearedState, indexName);
      await navigate(url);
    } catch (error) {
      // Silently handle navigation errors
    }
  };

  // Handler for browsing all jobs
  const handleBrowseAll = async () => {
    try {
      const indexName = getIndexName(intl.locale);
      const clearedState = {
        query: '',
        refinementList: {},
        page: 0,
        sort: 'relevance' as const,
      };
      
      pushIndexUiState(clearedState);
      const url = generateUrlFromIndexUiState(clearedState, indexName);
      await navigate(url);
    } catch (error) {
      // Silently handle navigation errors
    }
  };

  // Get active filter labels for display
  const activeFilterLabels = useMemo(() => {
    const labels: Array<{ attribute: string; value: string; label: string }> = [];
    Object.entries(filtersObj).forEach(([attribute, values]) => {
      if (Array.isArray(values)) {
        values.forEach(value => {
          // Try to find the label from facets first (has correct casing)
          const facetItems = facetsData[attribute] || [];
          const facetItem = facetItems.find(item => item.value === value);
          const label = facetItem?.label || formatFilterLabel(attribute, value);
          labels.push({ attribute, value, label });
        });
      }
    });
    return labels;
  }, [filtersObj, facetsData]);

  return (
    <div className={`min-h-[50vh] w-full flex flex-col items-center justify-center py-6 px-4 ${iosFontStack} bg-transparent`}>
      <div className="w-full max-w-4xl mx-auto">
        {/* Main Card */}
        <div className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-3xl shadow-2xl border border-gray-200/60 dark:border-gray-700/60 p-6 sm:p-8">
          {/* Header Section */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              <FormattedMessage
                defaultMessage="No results found"
                description="No results message"
                id="search.noresults.title"
              />
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm text-center max-w-md mb-4">
              <FormattedMessage
                defaultMessage="Try adjusting your search or filters to find more opportunities."
                description="Helpful message for no results"
                id="search.noresults.description"
              />
            </p>
          </div>

          {/* Search Parameters Section - Shows what caused no results */}
          {(query || hasFilters || isNotFirstPage) && (
            <div className="mb-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-3">
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  <FormattedMessage
                    defaultMessage="Your Search Parameters"
                    description="Label for search parameters section"
                    id="search.noresults.searchParameters"
                  />
                </h3>
              </div>
              
              <div className="space-y-3">
                {/* Query */}
                {query && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        <FormattedMessage
                          defaultMessage="Query:"
                          description="Label for search query"
                          id="search.noresults.queryLabel"
                        />
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">"{query}"</span>
                    </div>
                    <button
                      onClick={handleClearQuery}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer group"
                      type="button"
                    >
                      <FormattedMessage
                        defaultMessage="Remove"
                        description="Button to remove query"
                        id="search.noresults.removeQuery"
                      />
                      <X className="w-3 h-3 group-hover:text-red-500 transition-colors" />
                    </button>
                  </div>
                )}

                {/* Filters */}
                {hasFilters && activeFilterLabels.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        <FormattedMessage
                          defaultMessage="Filters:"
                          description="Label for filters"
                          id="search.noresults.filtersLabel"
                        />
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeFilterLabels.map(({ attribute, value, label }, index) => (
                        <button
                          key={`${attribute}-${value}-${index}`}
                          onClick={() => handleRemoveFilter(attribute, value)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer group"
                          type="button"
                        >
                          <span>{label}</span>
                          <X className="w-3 h-3 group-hover:text-red-500 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Page Number */}
                {isNotFirstPage && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        <FormattedMessage
                          defaultMessage="Page:"
                          description="Label for page number"
                          id="search.noresults.pageLabel"
                        />
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {page + 1}
                      </span>
                    </div>
                    <button
                      onClick={handleGoToFirstPage}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer group"
                      type="button"
                    >
                      <FormattedMessage
                        defaultMessage="Go to page 1"
                        description="Button to go to first page"
                        id="search.noresults.goToFirstPage"
                      />
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Clear All Button */}
                {(query || hasFilters || isNotFirstPage) && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleClearSearch}
                      className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium flex items-center gap-1 transition-colors cursor-pointer"
                      type="button"
                    >
                      <FormattedMessage
                        defaultMessage="Clear all search parameters"
                        description="Button to clear all search parameters"
                        id="search.noresults.clearAllParameters"
                      />
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Query Suggestions Section */}
          {suggestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                <FormattedMessage
                  defaultMessage="Try these searches:"
                  description="Heading for query suggestions"
                  id="search.noresults.trySearches"
                />
              </h3>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.query}-${index}`}
                    onClick={() => handleSuggestionClick(suggestion.query)}
                    className="px-4 py-2 rounded-full text-sm font-medium shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300 hover:border-green-300 dark:hover:border-green-700 border border-transparent transition-all duration-200 cursor-pointer"
                    type="button"
                  >
                    {suggestion.query}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Popular Categories Section */}
          {(popularIndustries.length > 0 || popularRoles.length > 0 || popularLocations.length > 0) && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-green-600 dark:text-green-400" />
                <FormattedMessage
                  defaultMessage="Popular Categories"
                  description="Heading for popular categories"
                  id="search.noresults.popularCategories"
                />
              </h3>
              
              {/* Industries */}
              {popularIndustries.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                    <FormattedMessage
                      defaultMessage="Industries"
                      description="Label for industries section"
                      id="search.noresults.industries"
                    />
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {popularIndustries.map((industry) => (
                      <button
                        key={industry.label}
                        onClick={() => handleFilterClick('companyIndustry', industry.label)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700 hover:text-green-700 dark:hover:text-green-300 transition-all duration-200 cursor-pointer group text-sm"
                        type="button"
                      >
                        {getIndustryIcon(industry.label)}
                        <span className="font-medium">{industry.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                          {industry.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Roles */}
              {popularRoles.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                    <FormattedMessage
                      defaultMessage="Job Roles"
                      description="Label for job roles section"
                      id="search.noresults.jobRoles"
                    />
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {popularRoles.map((role) => (
                      <button
                        key={role.label}
                        onClick={() => handleFilterClick('normalisedTitle', role.label)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700 hover:text-green-700 dark:hover:text-green-300 transition-all duration-200 cursor-pointer group text-sm"
                        type="button"
                      >
                        {getRoleIcon(role.label)}
                        <span className="font-medium">{role.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                          {role.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Locations */}
              {popularLocations.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                    <FormattedMessage
                      defaultMessage="Locations"
                      description="Label for locations section"
                      id="search.noresults.locations"
                    />
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {popularLocations.map((location) => (
                      <button
                        key={location.label}
                        onClick={() => handleFilterClick('location', location.label)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700 hover:text-green-700 dark:hover:text-green-300 transition-all duration-200 cursor-pointer group text-sm"
                        type="button"
                      >
                        <MapPin className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                        <span className="font-medium">{location.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                          {location.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Tips Section */}
          <div className="mb-6 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
                  <FormattedMessage
                    defaultMessage="Search Tips"
                    description="Heading for search tips"
                    id="search.noresults.searchTips"
                  />
                </h3>
                <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                    <span>
                      <FormattedMessage
                        defaultMessage="Try using broader search terms or remove some filters"
                        description="Search tip 1"
                        id="search.noresults.tip1"
                      />
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                    <span>
                      <FormattedMessage
                        defaultMessage="Check your spelling or try alternative keywords"
                        description="Search tip 2"
                        id="search.noresults.tip2"
                      />
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                    <span>
                      <FormattedMessage
                        defaultMessage="Browse popular categories above to discover new opportunities"
                        description="Search tip 3"
                        id="search.noresults.tip3"
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleBrowseAll}
              className="flex-1 px-5 py-3 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white font-semibold text-sm rounded-xl shadow-lg transition-colors duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer flex items-center justify-center gap-2 transition-transform"
              type="button"
            >
              <Briefcase className="w-4 h-4" />
              <FormattedMessage
                defaultMessage="Browse All Jobs"
                description="Button to browse all jobs"
                id="search.noresults.browseAll"
              />
            </button>
            <button
              onClick={handleClearSearch}
              className="flex-1 px-5 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 font-semibold text-sm rounded-xl shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-gray-400 cursor-pointer flex items-center justify-center gap-2"
              type="button"
            >
              <X className="w-4 h-4" />
              <FormattedMessage
                defaultMessage="Clear Search"
                description="Button to clear all search filters and query"
                id="search.noresults.clearSearch"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
